/**
 * "Connecting an identity provider" — Auth section (ADR-0074).
 * Reconciled from FederationSettingsPage/idp-fields.ts + the server Zod schemas
 * (idp-config-schemas.ts) and idp-config.graphql. Per-IdP required fields are
 * the exact field set the dashboard form collects and the mutation validates.
 */
import type { DocPageContent } from '../types';

export const AUTH_IDENTITY_PROVIDERS_PAGE: DocPageContent = {
  slug: 'auth/identity-providers',
  titleKey: 'docs.auth.providers.title',
  descriptionKey: 'docs.auth.providers.description',
  blocks: [
    { kind: 'paragraph', textKey: 'docs.auth.providers.lead' },

    { kind: 'heading', level: 2, id: 'where', textKey: 'docs.auth.providers.where.heading' },
    { kind: 'paragraph', textKey: 'docs.auth.providers.where.body' },
    {
      kind: 'callout',
      tone: 'note',
      titleKey: 'docs.auth.providers.env.title',
      textKey: 'docs.auth.providers.env.body',
      links: {
        env: {
          target: { docSlug: 'dashboard/environments' },
          labelKey: 'docs.auth.providers.env.link',
        },
      },
    },

    {
      kind: 'heading',
      level: 2,
      id: 'supported',
      textKey: 'docs.auth.providers.supported.heading',
    },
    { kind: 'paragraph', textKey: 'docs.auth.providers.supported.body' },

    { kind: 'heading', level: 3, id: 'auth0', textKey: 'docs.auth.providers.auth0.heading' },
    { kind: 'paragraph', textKey: 'docs.auth.providers.auth0.body' },
    {
      kind: 'definitions',
      items: [
        {
          termKey: 'docs.auth.providers.auth0.field.domain.term',
          descKey: 'docs.auth.providers.auth0.field.domain.desc',
        },
        {
          termKey: 'docs.auth.providers.auth0.field.clientId.term',
          descKey: 'docs.auth.providers.auth0.field.clientId.desc',
        },
        {
          termKey: 'docs.auth.providers.auth0.field.audience.term',
          descKey: 'docs.auth.providers.auth0.field.audience.desc',
        },
      ],
    },

    { kind: 'heading', level: 3, id: 'clerk', textKey: 'docs.auth.providers.clerk.heading' },
    { kind: 'paragraph', textKey: 'docs.auth.providers.clerk.body' },
    {
      kind: 'definitions',
      items: [
        {
          termKey: 'docs.auth.providers.clerk.field.instanceUrl.term',
          descKey: 'docs.auth.providers.clerk.field.instanceUrl.desc',
        },
        {
          termKey: 'docs.auth.providers.clerk.field.clientId.term',
          descKey: 'docs.auth.providers.clerk.field.clientId.desc',
        },
      ],
    },

    { kind: 'heading', level: 3, id: 'cognito', textKey: 'docs.auth.providers.cognito.heading' },
    { kind: 'paragraph', textKey: 'docs.auth.providers.cognito.body' },
    {
      kind: 'definitions',
      items: [
        {
          termKey: 'docs.auth.providers.cognito.field.domain.term',
          descKey: 'docs.auth.providers.cognito.field.domain.desc',
        },
        {
          termKey: 'docs.auth.providers.cognito.field.clientId.term',
          descKey: 'docs.auth.providers.cognito.field.clientId.desc',
        },
        {
          termKey: 'docs.auth.providers.cognito.field.region.term',
          descKey: 'docs.auth.providers.cognito.field.region.desc',
        },
        {
          termKey: 'docs.auth.providers.cognito.field.userPoolId.term',
          descKey: 'docs.auth.providers.cognito.field.userPoolId.desc',
        },
      ],
    },

    { kind: 'heading', level: 3, id: 'workos', textKey: 'docs.auth.providers.workos.heading' },
    { kind: 'paragraph', textKey: 'docs.auth.providers.workos.body' },
    {
      kind: 'definitions',
      items: [
        {
          termKey: 'docs.auth.providers.workos.field.clientId.term',
          descKey: 'docs.auth.providers.workos.field.clientId.desc',
        },
        {
          termKey: 'docs.auth.providers.workos.field.organizationId.term',
          descKey: 'docs.auth.providers.workos.field.organizationId.desc',
        },
      ],
    },

    { kind: 'heading', level: 3, id: 'keycloak', textKey: 'docs.auth.providers.keycloak.heading' },
    { kind: 'paragraph', textKey: 'docs.auth.providers.keycloak.body' },
    {
      kind: 'definitions',
      items: [
        {
          termKey: 'docs.auth.providers.keycloak.field.baseUrl.term',
          descKey: 'docs.auth.providers.keycloak.field.baseUrl.desc',
        },
        {
          termKey: 'docs.auth.providers.keycloak.field.realm.term',
          descKey: 'docs.auth.providers.keycloak.field.realm.desc',
        },
        {
          termKey: 'docs.auth.providers.keycloak.field.clientId.term',
          descKey: 'docs.auth.providers.keycloak.field.clientId.desc',
        },
      ],
    },

    { kind: 'heading', level: 3, id: 'entra', textKey: 'docs.auth.providers.entra.heading' },
    { kind: 'paragraph', textKey: 'docs.auth.providers.entra.body' },
    {
      kind: 'definitions',
      items: [
        {
          termKey: 'docs.auth.providers.entra.field.tenantId.term',
          descKey: 'docs.auth.providers.entra.field.tenantId.desc',
        },
        {
          termKey: 'docs.auth.providers.entra.field.clientId.term',
          descKey: 'docs.auth.providers.entra.field.clientId.desc',
        },
        {
          termKey: 'docs.auth.providers.entra.field.where.term',
          descKey: 'docs.auth.providers.entra.field.where.desc',
        },
      ],
    },

    {
      kind: 'callout',
      tone: 'note',
      titleKey: 'docs.auth.providers.resources.title',
      textKey: 'docs.auth.providers.resources.body',
      links: {
        auth0: {
          target: { href: 'https://auth0.com/docs', external: true },
          labelKey: 'docs.auth.providers.resources.link.auth0',
        },
        clerk: {
          target: { href: 'https://clerk.com/docs', external: true },
          labelKey: 'docs.auth.providers.resources.link.clerk',
        },
        cognito: {
          target: { href: 'https://docs.aws.amazon.com/cognito/', external: true },
          labelKey: 'docs.auth.providers.resources.link.cognito',
        },
        workos: {
          target: { href: 'https://workos.com/docs', external: true },
          labelKey: 'docs.auth.providers.resources.link.workos',
        },
      },
    },

    { kind: 'heading', level: 2, id: 'beyond', textKey: 'docs.auth.providers.beyond.heading' },
    { kind: 'paragraph', textKey: 'docs.auth.providers.beyond.body' },
    {
      kind: 'definitions',
      items: [
        {
          termKey: 'docs.auth.providers.beyond.oidc.term',
          descKey: 'docs.auth.providers.beyond.oidc.desc',
        },
        {
          termKey: 'docs.auth.providers.beyond.customJwt.term',
          descKey: 'docs.auth.providers.beyond.customJwt.desc',
        },
        {
          termKey: 'docs.auth.providers.beyond.introspection.term',
          descKey: 'docs.auth.providers.beyond.introspection.desc',
        },
        {
          termKey: 'docs.auth.providers.beyond.bff.term',
          descKey: 'docs.auth.providers.beyond.bff.desc',
        },
      ],
    },
    {
      kind: 'callout',
      tone: 'note',
      titleKey: 'docs.auth.providers.beyond.note.title',
      textKey: 'docs.auth.providers.beyond.note.body',
      links: {
        signed: {
          target: { docSlug: 'auth/end-user-auth' },
          labelKey: 'docs.auth.providers.beyond.note.link',
        },
      },
    },

    { kind: 'heading', level: 2, id: 'offline', textKey: 'docs.auth.providers.offline.heading' },
    { kind: 'paragraph', textKey: 'docs.auth.providers.offline.body' },
    {
      kind: 'callout',
      tone: 'tip',
      titleKey: 'docs.auth.providers.offline.note.title',
      textKey: 'docs.auth.providers.offline.note.body',
      links: {
        stepup: {
          target: { docSlug: 'auth/step-up' },
          labelKey: 'docs.auth.providers.offline.note.link',
        },
      },
    },

    { kind: 'heading', level: 2, id: 'status', textKey: 'docs.auth.providers.status.heading' },
    {
      kind: 'callout',
      tone: 'note',
      titleKey: 'docs.auth.providers.status.title',
      textKey: 'docs.auth.providers.status.body',
    },

    { kind: 'heading', level: 2, id: 'next', textKey: 'docs.common.nextSteps' },
    {
      kind: 'cardGrid',
      cards: [
        {
          titleKey: 'docs.auth.providers.next.mcp.title',
          textKey: 'docs.auth.providers.next.mcp.text',
          to: { docSlug: 'mcp/authentication' },
        },
        {
          titleKey: 'docs.auth.providers.next.keys.title',
          textKey: 'docs.auth.providers.next.keys.text',
          to: { docSlug: 'auth/api-keys' },
        },
      ],
    },
  ],
};
